import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { RotateCcw } from 'lucide-react';
import type { PipelineStep } from '@/api/alibaba';
import { cn } from '@/lib/utils';

interface PipelineGanttProps {
  steps: PipelineStep[];
  onRetry: (stepId: string) => void;
}

const SERIES_STYLE: Record<string, { bg: string; text: string; badge: 'default' | 'secondary' | 'outline' }> = {
  A: { bg: 'bg-blue-50', text: 'text-blue-700', badge: 'default' },
  B: { bg: 'bg-orange-50', text: 'text-orange-700', badge: 'secondary' },
  C: { bg: 'bg-purple-50', text: 'text-purple-700', badge: 'outline' },
};

const STATUS_BADGE: Record<string, { label: string; className: string }> = {
  completed: { label: '完成', className: 'bg-green-100 text-green-700 border-green-200' },
  failed: { label: '失敗', className: 'bg-red-100 text-red-700 border-red-200' },
  running: { label: '執行中', className: 'bg-blue-100 text-blue-700 border-blue-200 animate-pulse' },
  pending: { label: '待處理', className: 'bg-gray-100 text-gray-600 border-gray-200' },
  skipped: { label: '已跳過', className: 'bg-gray-100 text-gray-400 border-gray-200' },
};

function formatDuration(ms: number | null): string {
  if (ms === null) return '—';
  if (ms < 1000) return `${ms}ms`;
  if (ms < 60_000) return `${(ms / 1000).toFixed(1)}s`;
  const m = Math.floor(ms / 60_000);
  const s = Math.round((ms % 60_000) / 1000);
  return `${m}m ${s}s`;
}

function formatTime(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleTimeString('zh-TW', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
}

export function PipelineGantt({ steps, onRetry }: PipelineGanttProps) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b text-left text-muted-foreground">
            <th className="px-3 py-2 w-20">步驟</th>
            <th className="px-3 py-2">名稱</th>
            <th className="px-3 py-2 w-20">狀態</th>
            <th className="px-3 py-2 w-24">開始</th>
            <th className="px-3 py-2 w-24">完成</th>
            <th className="px-3 py-2 w-20 text-right">耗時</th>
            <th className="px-3 py-2">輸出</th>
            <th className="px-3 py-2 w-16">操作</th>
          </tr>
        </thead>
        <tbody>
          {steps.map(step => {
            const series = SERIES_STYLE[step.series] || SERIES_STYLE.A;
            const statusBadge = STATUS_BADGE[step.status] || STATUS_BADGE.pending;
            const isFailed = step.status === 'failed';

            return (
              <tr
                key={step.step_id}
                className={cn(
                  'border-b transition-colors hover:bg-accent/50',
                  isFailed && 'bg-red-50/50',
                  series.bg,
                )}
              >
                <td className={cn('px-3 py-2 font-mono font-semibold', series.text)}>
                  {step.step_id}
                </td>
                <td className="px-3 py-2">{step.step_name}</td>
                <td className="px-3 py-2">
                  <Badge variant="outline" className={cn('text-[11px] px-1.5', statusBadge.className)}>
                    {statusBadge.label}
                  </Badge>
                </td>
                <td className="px-3 py-2 font-mono text-xs">{formatTime(step.started_at)}</td>
                <td className="px-3 py-2 font-mono text-xs">{formatTime(step.completed_at)}</td>
                <td className="px-3 py-2 font-mono text-xs text-right">{formatDuration(step.duration_ms)}</td>
                <td className="px-3 py-2 text-xs text-muted-foreground max-w-[200px] truncate">
                  {isFailed ? (
                    <span className="text-red-600">{step.error_message}</span>
                  ) : (
                    step.output_summary || '—'
                  )}
                </td>
                <td className="px-3 py-2">
                  {isFailed && (
                    <Button size="sm" variant="ghost" className="h-6 w-6 p-0" onClick={() => onRetry(step.step_id)}>
                      <RotateCcw className="h-3.5 w-3.5" />
                    </Button>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
